<script
	lang="ts"
	module
>
	import type { Model, InventoryType, Manufacturer, SearchField } from 'utility/search-fields'
	export type Type = 'ASSEMBLY' | 'PART' | 'VEHICLE'

	//end of module section
</script>

<script lang="ts">
	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import { startsWithVowel } from 'utility/language-tools'
	import { loadPartModelsForInventoryTypeAndManufacturer } from 'utility/search-fields'
	import { untrack, type ComponentProps } from 'svelte'

	interface Props
		extends Omit<ComponentProps<typeof Autocomplete<Model | null>>, 'value' | 'options' | 'getLabel' | 'type'> {
		model?: Model | null
		inventoryType?: InventoryType | null
		manufacturer: Manufacturer | null
		initialValue?: number
		modelList?: Promise<Model[]> | Model[]
		type: Type // no default, require that they pass it
		disabled?: boolean
	}

	let {
		model = $bindable(null),
		inventoryType = null,
		manufacturer,
		modelList = $bindable([]),
		type,
		disabled = false,
		initialValue,
		...rest
	}: Props = $props()

	async function loadModels({
		inventoryType,
		manufacturer,
	}: {
		inventoryType: InventoryType | null
		manufacturer: Manufacturer | null
	}) {
		if (inventoryType || manufacturer) {
			modelList = loadPartModelsForInventoryTypeAndManufacturer({
				inventoryTypeId: type === 'ASSEMBLY' ? inventoryType?.setId ?? inventoryType?.id : inventoryType?.id,
				manufacturerId: manufacturer?.id,
			})
			modelList.then(loadedModels => {
				if (initialValue) {
					model = loadedModels.find(m => m.id === initialValue) ?? null
				}
				//If we have a selected model but after loading the new list, it's not in the list, then we need to the clear the selection
				else if (model && !loadedModels.find(loadedModel => loadedModel.id === model?.id)) {
					model = null
				}
			})
		} else {
			modelList = []
			model = null
		}
	}

	function computePlaceholder(type: Type, inventoryType: InventoryType | null, manufacturer: Manufacturer | null) {
		const typeLabel = typeLabelMap.get(type)
		if (inventoryType && !manufacturer) {
			return `Select ${startsWithVowel(typeLabel?.parentLabel ?? '') ? 'an' : 'a'} ${
				typeLabel?.parentLabel ?? ''
			} to shorten this list`
		} else if (!inventoryType && type !== 'VEHICLE') {
			return 'Select an inventory type first'
		} else {
			return `Select ${startsWithVowel(typeLabel?.label ?? '') ? 'an' : 'a'} ${typeLabel?.label ?? ''}`
		}
	}

	interface Label {
		label: string
		parentLabel: string
	}
	const typeLabelMap = new Map<Type, Label>([
		[
			'ASSEMBLY',
			{
				label: 'Assembly Model',
				parentLabel: 'Assembly Manufacturer',
			},
		],
		[
			'PART',
			{
				label: 'Part Model',
				parentLabel: 'Part Manufacturer',
			},
		],
		[
			'VEHICLE',
			{
				label: 'Vehicle Model',
				parentLabel: 'Vehicle Make',
			},
		],
	])

	let placeholder = $derived(computePlaceholder(type, inventoryType, manufacturer))
	let label = $derived(typeLabelMap.get(type)?.label ?? 'Model')

	$effect(() => {
		inventoryType
		manufacturer
		untrack(() => loadModels({ inventoryType, manufacturer }))
	})
</script>

<Autocomplete
	{...rest}
	{label}
	getLabel={option => option?.name ?? ''}
	options={modelList}
	bind:value={model}
	emptyValue={null}
	{placeholder}
	disabled={disabled || !(inventoryType || manufacturer)}
></Autocomplete>
